import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Modal, message } from 'antd';
import { refreshTrends } from '@store/common/actions';
import { clearCache } from '@src/api/cacheAdapter/config';
import useKpiDeletionUsage from '@src/hooks/useKpiDeletionUsage';
import useKPI from '@src/hooks/useKPI';
import { OVERVIEW_LABELS } from '@src/utils/kpi';

const KpiDeleteModal = ({ kpiValues, currentKpi, isModalOpen, discardDeleteHandler, panelRef }) => {
  const dispatch = useDispatch();
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const { canDelete, usageWarning, setUsageWarning, isChecking, hasError, retry } =
    useKpiDeletionUsage(kpiValues.kpiID, isModalOpen);
  const { deleteKpiMutation } = useKPI();
  const { ACTIVE_KPI_DELETE_CONFIRMATION, OTHER_KPI_DELETE_CONFIRMATION } = OVERVIEW_LABELS;

  const setDeletedKpiHandler = () => {
    if (!canDelete || isDeleteLoading) return;
    setIsDeleteLoading(true);
    deleteKpiMutation.mutate(kpiValues.kpiID, {
      onSuccess: () => {
        message.info(`${kpiValues.kpiName} KPI has been deleted successfully.`);
        setIsDeleteLoading(false);
        discardDeleteHandler();
        if (kpiValues.kpiID === currentKpi?.kpiID) {
          clearCache(); // clear axios cache as threshold changed
          dispatch(refreshTrends());
        }
      },
      onError: (error: any) => {
        if (error?.response?.data?.code === 'KPI_IN_USE_BY_AI_ALERTS') {
          setUsageWarning(error.response.data.message);
          setIsDeleteLoading(false);
          return;
        }
        message.error('There was an error deleting KPI. Please try again later.');
        discardDeleteHandler();
        setIsDeleteLoading(false);
      },
    });
  };

  const footerButtons = [
    <Button key="discard" onClick={discardDeleteHandler}>
      Discard
    </Button>,
    <Button
      key="save"
      type="primary"
      onClick={setDeletedKpiHandler}
      loading={isDeleteLoading}
      disabled={!canDelete || isDeleteLoading}
    >
      Proceed
    </Button>,
  ];

  return (
    <Modal
      width="500px"
      title="Delete Custom KPI"
      open={isModalOpen}
      onCancel={discardDeleteHandler}
      footer={footerButtons}
      wrapClassName="kpi-delete-modal"
      getContainer={() => panelRef.current}
    >
      {hasError ? (
        <Alert
          type="error"
          showIcon
          message="Unable to check whether this KPI is used by AI Alerts. Please retry."
          action={<Button onClick={retry}>Retry</Button>}
        />
      ) : usageWarning ? (
        <Alert type="warning" showIcon message={usageWarning} />
      ) : (
        <p>
          {kpiValues.kpiID === currentKpi?.kpiID
            ? ACTIVE_KPI_DELETE_CONFIRMATION
            : OTHER_KPI_DELETE_CONFIRMATION}
        </p>
      )}
      {isChecking && <p role="status">Checking AI Alerts usage…</p>}
    </Modal>
  );
};

export default KpiDeleteModal;
